/**
 * AccessGateContext - Email Whitelist for Platform Access
 * 
 * Two-layer access control:
 * 1. Platform Access (this context): Email must be in allowed_emails table
 *    - If NOT whitelisted: Only Overview is accessible, rest shows upgrade popup
 *    - If whitelisted: Full platform access
 * 
 * 2. Feature Access (ProfilePlanContext): GMB profile features based on plan
 *    - basic/advance/pro plans control competitor limits, cooldowns, etc.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import logger from '../utils/logger';
import { getSession } from '../utils/authUtils';

const AccessGateContext = createContext(null);

export const AccessGateProvider = ({ children }) => {
  // Initialize from cache if available for immediate access
  const [isChecking, setIsChecking] = useState(() => {
    try {
      if (typeof window !== 'undefined') {
        const cached = localStorage.getItem('access_whitelisted');
        if (cached !== null) return false; // If we have a cached value, we are "done checking" initially (optimistic)
      }
    } catch (_) { }
    return true;
  });
  const [isWhitelisted, setIsWhitelisted] = useState(() => {
    try {
      if (typeof window !== 'undefined') {
        const cached = localStorage.getItem('access_whitelisted');
        return cached === 'true';
      }
    } catch (_) { }
    return false;
  });
  const [email, setEmail] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Check if the current user's email is whitelisted for platform access
   */
  const checkWhitelistStatus = useCallback(async () => {
    try {
      // Don't set isChecking(true) here if we already have a value, 
      // otherwise it flickers the lock state during re-validation
      // setIsChecking(true); 
      setError(null);

      // Get user session to find email
      const session = getSession();

      logger.log('[AccessGate] Session:', session ? { email: session.email, role_id: session.role_id } : 'null');

      if (!session || !session.email) {
        logger.log('[AccessGate] No session or email found');
        setIsWhitelisted(false);
        try { localStorage.removeItem('access_whitelisted'); } catch (_) { }
        setEmail(null);
        setIsChecking(false);
        return;
      }

      const userEmail = session.email.toLowerCase().trim();
      setEmail(userEmail);

      logger.log('[AccessGate] Checking whitelist for:', userEmail);

      // Only role_id 9 is admin and has full access bypass
      const adminRoles = [9];
      if (adminRoles.includes(session.role_id) || adminRoles.includes(Number(session.role_id))) {
        logger.log('[AccessGate] Admin user (role_id 9) - full access granted');
        setIsWhitelisted(true);
        try { localStorage.setItem('access_whitelisted', 'true'); } catch (_) { }
        setLastChecked(new Date());
        setIsChecking(false);
        return;
      }

      // Check whitelist via API
      try {
        console.log('🔐 [AccessGate] Making API call for email:', userEmail);
        const response = await api.post('/access/check-whitelist', { email: userEmail });

        console.log('🔐 [AccessGate] Raw API Response:', response);
        console.log('🔐 [AccessGate] Response type:', typeof response);
        console.log('🔐 [AccessGate] Response keys:', response ? Object.keys(response) : 'null');

        // The API returns the JSON directly (not wrapped in data)
        // Response structure: { success: true, isWhitelisted: true/false, message: "..." }
        let whitelisted = false;

        if (typeof response === 'object' && response !== null) {
          // Direct response from API
          if ('isWhitelisted' in response) {
            whitelisted = response.isWhitelisted === true;
            console.log('🔐 [AccessGate] Found isWhitelisted directly:', response.isWhitelisted);
          }
          // Wrapped in data (some API structures)
          else if (response.data && 'isWhitelisted' in response.data) {
            whitelisted = response.data.isWhitelisted === true;
            console.log('🔐 [AccessGate] Found isWhitelisted in data:', response.data.isWhitelisted);
          }
        }

        console.log('🔐 [AccessGate] FINAL RESULT - Email:', userEmail, 'isWhitelisted:', whitelisted);

        setIsWhitelisted(whitelisted);
        try { localStorage.setItem('access_whitelisted', String(whitelisted)); } catch (_) { }
        setLastChecked(new Date());
      } catch (apiErr) {
        console.error('🔐 [AccessGate] API call failed:', apiErr);
        // On API error, deny access by default for security
        console.warn('🔐 [AccessGate] API error - denying access as fallback');
        setIsWhitelisted(false);
        setError(apiErr.message);
      }
    } catch (err) {
      logger.error('[AccessGate] Error checking whitelist:', err);
      setError(err.message);
      // On error, default to NOT whitelisted for security
      setIsWhitelisted(false);
    } finally {
      setIsChecking(false);
    }
  }, []);

  /**
   * Refresh whitelist status (can be called after admin adds email)
   */
  const refreshAccess = useCallback(async () => {
    await checkWhitelistStatus();
  }, [checkWhitelistStatus]);

  // Check whitelist status on mount and when session changes
  useEffect(() => {
    checkWhitelistStatus();

    // Also check when storage changes (other tabs) or when sessionChanged event fires (same tab)
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'token') {
        checkWhitelistStatus();
      }
    };

    const handleSessionChanged = (e) => {
      // sessionChanged is a CustomEvent dispatched on login/logout in same tab
      checkWhitelistStatus();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('sessionChanged', handleSessionChanged);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sessionChanged', handleSessionChanged);
    };
  }, [checkWhitelistStatus]);

  const value = {
    isChecking,
    isWhitelisted,
    email,
    lastChecked,
    error,
    refreshAccess
  };

  return (
    <AccessGateContext.Provider value={value}>
      {children}
    </AccessGateContext.Provider>
  );
};

/**
 * Hook to access the AccessGate context
 */
export const useAccessGate = () => {
  const context = useContext(AccessGateContext);

  if (!context) {
    // Return a default "checking" state if context is not available
    return {
      isChecking: true,
      isWhitelisted: false,
      email: null,
      lastChecked: null,
      error: null,
      refreshAccess: async () => { }
    };
  }

  return context;
};

export default AccessGateContext;
